<template>
  <div class="tr-block">
    <div class="tr-block__top">
      <div v-html="transfer.type.icon" class="tr-block__type-icon"></div>
      <button
        v-if="is_general_manager || is_transfer_supervisor || is_transfer_manager || is_transfer_assistant"
        @click="onCopyTransferInfo(transfer)"
        type="button"
        class="tr-block__name"
      >
        {{ transfer.name }} ({{ transfer.pax }})
      </button>
      <div v-else class="tr-block__name">{{ transfer.name }} ({{ transfer.pax }})</div>
      <div class="tr-block__route">
        <span>{{ transfer.pick_up_point.short_name }}</span>
        <img src="@/assets/img/arrow-right-gray-icon.svg" alt="">
        <span v-for="(point, index) in transfer.points" :key="point.id" class="tr-block__route-group">
          <span>{{ point.location.short_name }}</span>
          <img v-if="index !== transfer.points.length - 1" src="@/assets/img/arrow-right-gray-icon.svg" alt="">
        </span>
      </div>
      <div v-if="transfer.status.code !== 'cancelled'" class="tr-block__top-right">
        <button
          v-if="is_transfer_managers && is_show_arrange_button"
          @click="onArrangeOpen(transfer)"
          type="button"
          class="tr-block__button"
        >
          <img src="@/assets/img/tr-arrange-icon.svg" alt="">
        </button>
        <template v-if="is_show_edit_cancel_buttons && showEditCancelButtons()">
          <button @click="onTriggerEditPopup(transfer)" class="tr-block__button" type="button">
            <img src="@/assets/img/tr-edit-icon.svg" alt="">
          </button>
          <button
            v-if="transfer.status.code === 'requested'"
            @click="onShowAcceptDeleteModal(transfer.id)"
            type="button"
            class="tr-block__button"
          >
            <img src="@/assets/img/tr-delete-icon.svg" alt="">
          </button>
          <button v-else @click="onShowAcceptModal(transfer.id)" class="tr-block__button" type="button">
            <img src="@/assets/img/tr-cancel-icon.svg" alt="">
          </button>
        </template>
      </div>
    </div>
    <button @click="onToggle" :disabled="details_loading" type="button" class="tr-block__bottom">
      <span class="tr-block__bottom-left">
        <span class="tr-block__arrival">
          <span class="tr-block__key">Pick-up:</span>
          <span class="tr-block__val">{{ transfer.pick_up_time }}</span>
        </span>
        <span class="tr-block__info-icons">
          <img v-if="has_signboard && !transfer.is_signboard_printed" src="@/assets/img/tr-signboard-not-printed-icon.svg" alt="">
          <img v-if="has_signboard && transfer.is_signboard_printed" src="@/assets/img/tr-signboard-printed-icon.svg" alt="">
          <img v-if="has_notes" src="@/assets/img/tr-note-icon.svg" alt="">
        </span>
      </span>
      <span v-if="transfer.status.code === 'arranged' && get_some_cars" class="tr-block__cars">
        <span class="tr-block__drive-time">
          <span class="tr-block__key">Start:</span>
          <span class="tr-block__val">{{ transfer.start_time }}</span>
        </span>
        <span class="tr-block__drive-time">
          <span class="tr-block__key">Finish:</span>
          <span class="tr-block__val">{{ transfer.estimated_finish_time }}</span>
        </span>
        <span class="tr-block__car">
          <img v-if="transfer.checkup.car_overlap === 'ok'" src="@/assets/img/tr-car-icon.svg" alt="">
          <img v-if="transfer.checkup.car_overlap !== 'ok'" src="@/assets/img/tr-car-overlap-icon.svg" alt="">
          <span class="tr-block__val">
            <template v-if="!get_some_cars.taxi_driver">
              {{ get_some_cars.car?.car_model.short_name }} ({{ get_some_cars.car?.plate_number }}) {{ count_of_cars > 1 ? `+${count_of_cars - 1}` : '' }}
            </template>
            <template v-else>
              {{ get_some_cars.taxi_driver?.car_model.short_name }} ({{ get_some_cars.taxi_driver?.plate_number }}) {{ count_of_cars > 1 ? `+${count_of_cars - 1}` : '' }}
            </template>
          </span>
        </span>
        <span class="tr-block__driver">
          <img v-if="transfer.checkup.driver_overlap === 'ok'" src="@/assets/img/tr-driver-icon.svg" alt="">
          <img v-if="transfer.checkup.driver_overlap !== 'ok'" src="@/assets/img/tr-driver-overlap-icon.svg" alt="">
          <span class="tr-block__val">
            <template v-if="!get_some_cars.taxi_driver">
              {{ get_some_cars.driver?.full_name }}
            </template>
            <template v-else>
              {{ get_some_cars.taxi_driver?.full_name }}
            </template>
          </span>
        </span>
      </span>
      <span
        v-if="transfer.status.code !== 'arranged'"
        :style="{ backgroundColor: transfer.status.color || '#35a4ea' }"
        class="tr-block__status"
      >
        {{ transfer.status.name }}
      </span>
      <span class="tr-block__toggler" :class="{ 'tr-block__toggler--open': is_open }">
        <img src="@/assets/img/arrow-toggler-bottom-icon.svg" alt="">
      </span>
    </button>
    <transition name="slide-block">
      <div v-if="is_open" class="tr-block__dropdown">
        <div v-if="['arrival', 'departure', 'climbing', 'day_trip', 'safari'].includes(transfer.type.code)" class="tr-block__panel">
          <div v-if="transfer.type.code === 'arrival' || transfer.type.code === 'departure'" class="tr-block__details">
            Transport details: <span>{{ transfer.transport_method.name }}<i class="tr-block__divider">|</i>{{ transfer.transport_company ? transfer.transport_company.name : '' }}<i v-if="transfer.transport_company" class="tr-block__divider">|</i>{{ transfer.transport_flight_number || '' }}<i v-if="transfer.transport_flight_number" class="tr-block__divider">|</i>{{ dateFormat(transfer.transport_date) }}<i v-if="transfer.transport_time" class="tr-block__divider">|</i>{{ transfer.transport_time }}</span>
          </div>
          <div v-if="transfer.type.code === 'climbing'" class="tr-block__details">
            Climbing group: <router-link :to="{ name: 'climbing-groups-detail', params: { id: transfer.climbing_group.id } }" class="tr-block__link-bordered">{{ transfer.climbing_group.name }}</router-link>
          </div>
          <div v-if="transfer.type.code === 'day_trip'" class="tr-block__details">
            Day trip group: <router-link :to="{ name: 'day-trips-groups-detail', params: { id: transfer.day_trip.id } }" class="tr-block__link-bordered">{{ transfer.day_trip.name }}</router-link>
          </div>
          <div v-if="transfer.type.code === 'safari'" class="tr-block__details">
            Day trip group: <router-link :to="{ name: 'safari-groups-detail', params: { id: transfer.safari_group.id } }" class="tr-block__link-bordered">{{ transfer.safari_group.name }}</router-link>
          </div>
          <div class="tr-block__right">
            <div v-if="transfer.signboard" class="tr-block__print">
              <span>Signboard:</span>
              <button @click="onPrint(transfer.id)" type="button">
                <img src="@/assets/img/print-white-small-icon.svg" alt="">
                Print
              </button>
              <app-switcher @change="onChangeSignboard(transfer.id)" v-model="transfer.is_signboard_printed" />
            </div>
            <!--<button type="button" class="tr-block__link">-->
            <!--<img src="@/assets/img/link-blue-icon.svg" alt="">-->
            <!--</button>-->
          </div>
        </div>
        <div class="tr-block__info">
          <div
              v-if="transfer.other_pax && transfer.other_pax > 0"
              class="tr-block__subtitle"
              :class="{ 'tr-block__subtitle--no-m': !transfer.clients.length && !transfer.employees.length }"
          >
            {{ transfer.type.code === 'climbing' ? 'Crew:' : 'Other pax:' }} {{ transfer.other_pax }}
          </div>
          <template v-if="transfer.clients.length || transfer.employees.length">
            <div class="tr-block__subtitle">
              <span v-if="transfer.clients.length">Clients:</span>
              <span v-if="transfer.employees.length">Staff:</span>
            </div>
            <div class="tr-block__list">
              <template v-if="transfer.clients.length">
                <div v-for="(item, index) in transfer.clients" :key="index" class="tr-block__item">
                  {{ index + 1 }}. {{ item.first_name }} {{ item.last_name || '' }}
                </div>
              </template>
              <template v-if="transfer.employees.length">
                <div v-for="(item, index) in transfer.employees" :key="index" class="tr-block__item">
                  {{ index + 1 }}. {{ item.first_name }} {{ item.last_name || '' }}
                </div>
              </template>
            </div>
          </template>
        </div>
        <div v-if="transfer.notes" class="tr-block__notes">
          <div class="tr-block__note-icon">
            <img src="@/assets/img/note-green-big-icon.svg" alt="">
            <span>NOTE:</span>
          </div>
          <div class="tr-block__text">{{ transfer.notes }}</div>
        </div>
        <div v-if="transfer.own_cars.length || transfer.rental_cars.length || transfer.taxi.length" class="tr-block__transport">
          <div class="transport-list">
            <div class="transport-list__header">
              <div class="transport-list__row">
                <div class="transport-list__col">
                  Transport ({{ count_of_cars }})
                </div>
                <div class="transport-list__col">Driver</div>
                <div class="transport-list__col transport-list__col--center">Main phone</div>
                <div class="transport-list__col transport-list__col--center">Additional phone</div>
                <div class="transport-list__col transport-list__col--center">Status</div>
              </div>
            </div>
            <div class="transport-list__body">
              <div v-if="transfer.own_cars.length" class="transport-list__part">
                <div class="transport-list__name">Own cars</div>
                <div v-for="(item, index) in transfer.own_cars" :key="`own_${item.id}`" class="transport-list__wrapper">
                  <div class="transport-list__row">
                    <div class="transport-list__col">
                      {{ index + 1 }}. {{ item.car.car_model.name }} ({{ item.car.plate_number }})
                    </div>
                    <div class="transport-list__col">{{ item.driver.full_name }}</div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button
                            @click="onCopyContactLink(item.driver.phone)"
                            v-tooltip="{content: item.driver.phone, placement: 'top-start', offset: 10}"
                            type="button"
                            :class="{ 'grayscale': !item.driver.phone }"
                        >
                          <img v-if="item.driver.phone" src="@/assets/img/phone-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a
                            :href="`https://t.me/${item.driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.phone_telegram }"
                        >
                          <img v-if="item.driver.phone_telegram" src="@/assets/img/tg-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/tg-round-gray-icon.svg" alt="">
                        </a>
                        <a
                            :href="`https://wa.me/${item.driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.phone_whatsapp }"
                        >
                          <img v-if="item.driver.phone_whatsapp" src="@/assets/img/wa-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/wa-round-gray-icon.svg" alt="">
                        </a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button type="button" class="grayscale">
                          <img src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a class="grayscale"><img src="@/assets/img/tg-round-gray-icon.svg" alt=""></a>
                        <a class="grayscale"><img src="@/assets/img/wa-round-gray-icon.svg" alt=""></a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--lh0 transport-list__col--center">
                      <div class="transport-list__status">
                        <img
                            v-if="item.driver_confirmed"
                            src="@/assets/img/confirmed-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'confirmed', placement: 'top-start', offset: 10}"
                        >
                        <img
                            v-else
                            src="@/assets/img/waiting-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'waiting confirmation', placement: 'top-start', offset: 10}"
                        >
                      </div>
                    </div>
                  </div>
                  <template v-if="transfer.status.code === 'arranged'">
                    <div v-if="item.car_overlap !== 'ok'" class="transport-list__error">{{ item.car_overlap }}</div>
                    <div v-if="item.driver_overlap !== 'ok'" class="transport-list__error">{{ item.driver_overlap }}</div>
                  </template>
                </div>
              </div>
              <div v-if="transfer.rental_cars.length" class="transport-list__part">
                <div class="transport-list__name">Rental cars</div>
                <div v-for="(item, index) in transfer.rental_cars" :key="`rental_${item.id}`" class="transport-list__wrapper">
                  <div class="transport-list__row">
                    <div class="transport-list__col">
                      {{ index + 1 }}. {{ item.rental_company.name }} - {{ item.car.car_model.name }} {{ item.car.plate_number ? `(${item.car.plate_number})` : '' }}
                    </div>
                    <div class="transport-list__col">{{ item.driver ? item.driver.full_name : '-' }}</div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button
                            @click="onCopyContactLink(item.driver.phone)"
                            v-tooltip="{content: item.driver.phone, placement: 'top-start', offset: 10}"
                            type="button"
                            :class="{ 'grayscale': !item.driver.phone }"
                        >
                          <img v-if="item.driver.phone" src="@/assets/img/phone-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a
                            :href="`https://t.me/${item.driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.phone_telegram }"
                        >
                          <img v-if="item.driver.phone_telegram" src="@/assets/img/tg-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/tg-round-gray-icon.svg" alt="">
                        </a>
                        <a
                            :href="`https://wa.me/${item.driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.phone_whatsapp }"
                        >
                          <img v-if="item.driver.phone_whatsapp" src="@/assets/img/wa-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/wa-round-gray-icon.svg" alt="">
                        </a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button
                            @click="onCopyContactLink(item.driver.additional_phone)"
                            v-tooltip="{content: item.driver.additional_phone, placement: 'top-start', offset: 10}"
                            type="button"
                            :class="{ 'grayscale': !item.driver.additional_phone }"
                        >
                          <img v-if="item.driver.additional_phone" src="@/assets/img/phone-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a
                            :href="`https://t.me/${item.driver.additional_phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.additional_phone_telegram }"
                        >
                          <img v-if="item.driver.additional_phone_telegram" src="@/assets/img/tg-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/tg-round-gray-icon.svg" alt="">
                        </a>
                        <a
                            :href="`https://wa.me/${item.driver.additional_phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.driver.additional_phone_whatsapp }"
                        >
                          <img v-if="item.driver.additional_phone_whatsapp" src="@/assets/img/wa-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/wa-round-gray-icon.svg" alt="">
                        </a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--lh0 transport-list__col--center">
                      <div class="transport-list__status">
                        <img
                            v-if="item.car && item.driver"
                            src="@/assets/img/confirmed-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'confirmed', placement: 'top-start', offset: 10}"
                        >
                        <img
                            v-else
                            src="@/assets/img/waiting-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'waiting confirmation', placement: 'top-start', offset: 10}"
                        >
                      </div>
                    </div>
                  </div>
                  <template v-if="transfer.status.code === 'arranged'">
                    <div v-if="item.car_overlap !== 'ok'" class="transport-list__error">{{ item.car_overlap }}</div>
                    <div v-if="item.driver_overlap !== 'ok'" class="transport-list__error">{{ item.driver_overlap }}</div>
                  </template>
                </div>
              </div>
              <div v-if="transfer.taxi.length" class="transport-list__part">
                <div class="transport-list__name">Taxi</div>
                <div v-for="(item, index) in transfer.taxi" :key="`taxi_${item.id}`" class="transport-list__wrapper">
                  <div class="transport-list__row">
                    <div class="transport-list__col">
                      {{ index + 1 }}. {{ item.taxi_driver.car_model.name }} {{ item.taxi_driver.plate_number ? `(${item.taxi_driver.plate_number})` : '' }}
                    </div>
                    <div class="transport-list__col">{{ item.taxi_driver.full_name }}</div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button
                            @click="onCopyContactLink(item.taxi_driver.phone)"
                            v-tooltip="{content: item.taxi_driver.phone, placement: 'top-start', offset: 10}"
                            type="button"
                            :class="{ 'grayscale': !item.taxi_driver.phone }"
                        >
                          <img v-if="item.taxi_driver.phone" src="@/assets/img/phone-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a
                            :href="`https://t.me/${item.taxi_driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.taxi_driver.phone_telegram }"
                        >
                          <img v-if="item.taxi_driver.phone_telegram" src="@/assets/img/tg-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/tg-round-gray-icon.svg" alt="">
                        </a>
                        <a
                            :href="`https://wa.me/${item.taxi_driver.phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.taxi_driver.phone_whatsapp }"
                        >
                          <img v-if="item.taxi_driver.phone_whatsapp" src="@/assets/img/wa-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/wa-round-gray-icon.svg" alt="">
                        </a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--center">
                      <div class="transport-list__socials">
                        <button
                            @click="onCopyContactLink(item.taxi_driver.additional_phone)"
                            v-tooltip="{content: item.taxi_driver.additional_phone, placement: 'top-start', offset: 10}"
                            type="button"
                            :class="{ 'grayscale': !item.taxi_driver.additional_phone }"
                        >
                          <img v-if="item.taxi_driver.additional_phone" src="@/assets/img/phone-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/phone-round-gray-icon.svg" alt="">
                        </button>
                        <a
                            :href="`https://t.me/${item.taxi_driver.additional_phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.taxi_driver.additional_phone_telegram }"
                        >
                          <img v-if="item.taxi_driver.additional_phone_telegram" src="@/assets/img/tg-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/tg-round-gray-icon.svg" alt="">
                        </a>
                        <a
                            :href="`https://wa.me/${item.taxi_driver.additional_phone}`"
                            target="_blank"
                            :class="{ 'grayscale': !item.taxi_driver.additional_phone_whatsapp }"
                        >
                          <img v-if="item.taxi_driver.additional_phone_whatsapp" src="@/assets/img/wa-round-icon.svg" alt="">
                          <img v-else src="@/assets/img/wa-round-gray-icon.svg" alt="">
                        </a>
                      </div>
                    </div>
                    <div class="transport-list__col transport-list__col--lh0 transport-list__col--center">
                      <div class="transport-list__status">
                        <img
                            v-if="item.driver_confirmed"
                            src="@/assets/img/confirmed-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'confirmed', placement: 'top-start', offset: 10}"
                        >
                        <img
                            v-else
                            src="@/assets/img/waiting-round-icon.svg"
                            alt=""
                            v-tooltip="{content: 'waiting confirmation', placement: 'top-start', offset: 10}"
                        >
                      </div>
                    </div>
                  </div>
                  <template v-if="transfer.status.code === 'arranged'">
                    <div v-if="item.car_overlap !== 'ok'" class="transport-list__error">{{ item.car_overlap }}</div>
                    <div v-if="item.driver_overlap !== 'ok'" class="transport-list__error">{{ item.driver_overlap }}</div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tr-block__manager">
          <span>Created by:</span>
          <img :src="getUserAvatar(transfer.created_by.crop.crop_url)" alt="Avatar">
          <span>{{ transfer.created_by.full_name }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { format, parse } from 'date-fns'
import { getTransferExpanded, getTransferSignboardPrint, patchTransfers } from '@/http'

export default {
  name: 'TransferBlock',
  props: {
    transfer: {
      type: Object
    },
    count: {
      type: Number,
      default: 0
    },
    is_show_arrange_button: {
      type: Boolean,
      default: true
    },
    is_show_edit_cancel_buttons: {
      type: Boolean,
      default: true
    },
    lazy_details: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      is_open: false,
      details_loaded: !this.lazy_details,
      details_loading: false,
      details_request: null,
    }
  },
  watch: {
    transfer(value, previousValue) {
      if (!this.lazy_details || value === previousValue) return
      this.details_loaded = Object.prototype.hasOwnProperty.call(value, 'clients')
      this.details_request = null
      this.is_open = false
    },
  },
  computed: {
    has_notes() {
      return this.transfer.has_notes || Boolean(this.transfer.notes)
    },
    has_signboard() {
      return this.transfer.has_signboard || Boolean(this.transfer.signboard)
    },
    get_some_cars() {
      if (this.transfer.own_cars.length || this.transfer.rental_cars.length || this.transfer.taxi.length) {
        if (this.transfer.own_cars.length) return this.transfer.own_cars[0]
        if (this.transfer.rental_cars.length) return this.transfer.rental_cars[0]
        if (this.transfer.taxi.length) return this.transfer.taxi[0]
      }
      return null
    },
    count_of_cars() {
      return this.transfer.own_cars.length + this.transfer.rental_cars.length + this.transfer.taxi.length
    },
    is_transfer_managers() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('transfer_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('transfer_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('transfer_assistant')
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_climbing_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor')
    },
    is_climbing_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('climbing_manager')
    },
    is_climbing_assistant() {
      return this.$store.state.userGroups.map(item => item.code).includes('climbing_assistant')
    },
    is_operations_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_manager')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    },
    is_transfer_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('transfer_manager')
    },
    is_transfer_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('transfer_supervisor')
    },
    is_transfer_assistant() {
      return this.$store.state.userGroups.map(item => item.code).includes('transfer_assistant')
    },
    is_safari_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor')
    },
    is_safari_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_manager')
    },
    is_safari_assistant() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_assistant')
    },
    is_hotel_reception() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reception')
    },
    is_hotel_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_supervisor')
    },
    is_hotel_reservations() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reservations')
    },
  },
  methods: {
    loadDetails() {
      if (this.details_loaded) return Promise.resolve(this.transfer)
      if (this.details_request) return this.details_request

      this.details_loading = true
      this.details_request = getTransferExpanded(this.transfer.id)
        .then(({ data }) => {
          Object.entries(data).forEach(([key, value]) => this.$set(this.transfer, key, value))
          this.details_loaded = true
          return this.transfer
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: 'Unable to load transfer details',
          })
          throw error
        })
        .finally(() => {
          this.details_loading = false
          this.details_request = null
        })

      return this.details_request
    },
    onToggle() {
      if (this.is_open) {
        this.is_open = false
        return
      }

      this.loadDetails().then(() => { this.is_open = true }).catch(() => {})
    },
    onCopyTransferInfo() {
      this.loadDetails().then(transfer => this.copyTransferInfo(transfer)).catch(() => {})
    },
    copyTransferInfo(transfer) {
      if (transfer) {
        let string =
          `🔹 You will have pick-up on ${this.dateFormat(transfer.pick_up_date)}\n\n👥 Group: ${transfer.name} (${transfer.pax})\n📌 Pick-up: ${transfer.pick_up_time} at ${transfer.pick_up_point.short_name}\n📍 To: ${transfer.points.map(point => point.location.short_name).join(' -> ')}`

        if (transfer.type.code === 'arrival' || transfer.type.code === 'departure') {
          string += `\n✈️ Transport details: ${transfer.transport_method.name} ${transfer.transport_company ? `| ${transfer.transport_company.name}` : ''} ${transfer.transport_flight_number ? `| ${transfer.transport_flight_number }` : ''} | ${this.dateFormat(transfer.transport_date)} | ${transfer.transport_time}`
        }

        if (transfer.clients.length) {
          string += `\n\n👤 Clients Names:\n\n${transfer.clients.map((client, index) => `${index + 1}. ${client.full_name}`).join('\n')}`
        }

        string += `\n\n🗒 Additional notes: ${transfer.notes || ''}`

        navigator.clipboard.writeText(string)
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Info copied to clipboard',
            })
          })
          .catch(() => { console.error('Something went wrong') })
      }
    },
    showEditCancelButtons() {
      if (this.transfer.type.code === 'arrival') return this.is_general_manager || this.is_sales_manager || this.is_sales_supervisor
      if (this.transfer.type.code === 'departure') return this.is_general_manager || this.is_sales_manager || this.is_sales_supervisor
      if (this.transfer.type.code === 'climbing') return this.is_general_manager || this.is_climbing_supervisor || this.is_climbing_manager || this.is_climbing_assistant || this.is_operations_supervisor
      if (this.transfer.type.code === 'day_trip') return this.is_general_manager || this.is_operations_manager || this.is_sales_manager || this.is_sales_supervisor
      if (this.transfer.type.code === 'other') return this.is_general_manager || this.is_sales_manager || this.is_sales_supervisor
      if (this.transfer.type.code === 'staff') return this.is_general_manager || this.is_operations_manager || this.is_transfer_manager || this.is_transfer_supervisor || this.is_transfer_assistant || this.is_operations_supervisor || this.is_hotel_reception || this.is_hotel_supervisor
      if (this.transfer.type.code === 'hotel_arrival') return this.is_general_manager || this.is_transfer_manager || this.is_transfer_supervisor || this.is_transfer_assistant || this.is_hotel_reception || this.is_hotel_supervisor || this.is_hotel_reservations
      if (this.transfer.type.code === 'hotel_departure') return this.is_general_manager || this.is_transfer_manager || this.is_transfer_supervisor || this.is_transfer_assistant || this.is_hotel_reception || this.is_hotel_supervisor || this.is_hotel_reservations
      if (this.transfer.type.code === 'hotel_other') return this.is_general_manager || this.is_transfer_manager || this.is_transfer_supervisor || this.is_transfer_assistant || this.is_hotel_reception || this.is_hotel_supervisor || this.is_hotel_reservations
      if (this.transfer.type.code === 'safari') return this.is_general_manager || this.is_safari_supervisor || this.is_safari_manager || this.is_safari_assistant
    },
    onTriggerEditPopup(transfer) {
      this.loadDetails().then(() => this.$emit('onOpenEditPopup', transfer)).catch(() => {})
    },
    onShowAcceptModal(id) {
      this.$emit('onCancelledTransfer', id)
    },
    onShowAcceptDeleteModal(id) {
      this.$emit('onDeleteTransfer', id)
    },
    onArrangeOpen(transfer) {
      this.loadDetails().then(() => this.$emit('onArrangePopupOpen', transfer)).catch(() => {})
    },
    onChangeSignboard(id) {
      patchTransfers(id, { is_signboard_printed: this.transfer.is_signboard_printed })
    },
    onPrint(id) {
      this.print_preload = true
      getTransferSignboardPrint(id)
        .finally(() => { this.print_preload = false })
        .then(response => { window.open(response.data) })
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>

<style lang="sass" scoped>
.tr-block
  width: 100%
  margin-bottom: 10px
  background-color: #fff
  border: 1px solid #dfdfdf
  border-radius: 10px
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)
  overflow: hidden

.tr-block__top
  display: flex
  align-items: center
  gap: 10px
  padding: 5px 7px 4px
  border-bottom: 1px solid #e8e8e8

.tr-block__top-right
  display: flex
  align-items: center
  gap: 8px

.tr-block__name
  max-width: 170px
  width: 100%
  color: #181818
  font-size: 14px
  font-weight: 500
  text-align: left
  transition: color 0.3s

  &:hover
    color: $color-theme

.tr-block__route
  flex-grow: 1
  display: flex
  align-items: center
  gap: 10px
  color: #181818
  font-weight: 500

.tr-block__route-group
  display: flex
  align-items: center
  gap: 10px

.tr-block__button
  line-height: 0
  box-shadow: 1px 3px 8px 0 rgba(0, 0, 0, 0.15)
  border-radius: 10px

.tr-block__toggler
  position: absolute
  top: 12px
  right: 14px
  line-height: 0
  transition: transform 0.3s

.tr-block__toggler--open
  transform: rotate(180deg)

.tr-block__bottom
  position: relative
  display: flex
  align-items: center
  gap: 10px
  width: 100%
  min-height: 30px
  padding: 5px 38px 4px 7px
  border-bottom: 1px solid #e8e8e8
  text-align: left
  font-size: 14px

.tr-block__status
  display: flex
  align-items: center
  justify-content: center
  width: 110px
  padding: 3px 0
  border-radius: 20px
  color: #fff
  font-weight: 600
  text-align: center
  font-size: 13px

.tr-block__type-icon
  line-height: 0

.tr-block__bottom-left
  display: flex
  align-items: center
  min-width: 205px

.tr-block__arrival
  display: flex
  align-items: center
  gap: 5px
  min-width: 106px

.tr-block__key
  color: #5f5f5f

.tr-block__val
  color: #181818
  font-weight: 500

.tr-block__info-icons
  display: inline-flex
  align-items: center
  gap: 5px
  margin-left: 18px

.tr-block__icons
  display: inline-flex
  align-items: center
  gap: 10px

.tr-block__cars
  display: grid
  grid-template-columns: 90px 90px 190px 1fr
  align-items: center
  gap: 25px

.tr-block__drive-time
  display: flex
  align-items: center
  gap: 5px

.tr-block__car
  display: flex
  align-items: center
  gap: 5px

.tr-block__driver
  display: flex
  align-items: center
  gap: 5px

.tr-block__dropdown
  background-color: #fafafa

.tr-block__panel
  display: flex
  align-items: center
  justify-content: space-between
  min-height: 35px
  padding-left: 17px

.tr-block__details
  color: #5e5e5e

  span
    margin-left: 10px
    color: #5f5f5f
    font-weight: 500

.tr-block__link-bordered
  display: inline-flex
  flex-direction: column
  width: max-content
  margin-left: 10px
  color: $color-text-base
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.tr-block__divider
  margin: 0 10px
  color: #5f5f5f
  font-style: normal

.tr-block__link
  padding: 6px 9px
  border-left: 1px solid #e8e8e8
  border-bottom-right-radius: 20px
  line-height: 0

.tr-block__right
  border-left: 1px solid #e8e8e8
  padding: 5px 0 5px 15px

.tr-block__print
  display: inline-flex
  align-items: center
  gap: 10px
  margin-right: 10px

  span
    font-size: 13px

  button
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 5px
    width: 64px
    height: 25px
    border-radius: 9px
    box-shadow: 1px 3px 8px rgba(0, 0, 0, 0.15)
    background-color: #ffA51f
    color: #fff
    font-size: 13px
    font-weight: 600

.tr-block__info
  padding: 13px 17px
  border-top: 1px solid #e8e8e8

.tr-block__subtitle
  margin-bottom: 10px
  font-weight: 500

.tr-block__subtitle--no-m
  margin-bottom: 0

.tr-block__item
  margin-bottom: 6px
  color: #181818

  &:last-child
    margin-bottom: 0

.tr-block__notes
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 18px 10px
  border-top: 1px solid #e8e8e8

.tr-block__note-icon
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 5px
  color: #181818
  font-weight: 500

.tr-block__text
  color: #5f5f5f

.tr-block__manager
  display: flex
  align-items: center
  justify-content: flex-end
  gap: 8px
  padding: 8px 10px
  border-top: 1px solid #e8e8e8

  span
    &:first-child
      color: #5f5f5f
      font-size: 13px

    &:last-child
      color: #181818
      font-size: 13px
      font-weight: 500

  img
    width: 25px
    height: 25px
    border-radius: 50%

.tr-block__transport
  border-top: 1px solid #e8e8e8

// ------------------------ transport-list
.transport-list
  border-top: 1px solid #e8e8e8

.transport-list__part
  border-bottom: 1px solid #e8e8e8

  &:last-child
    border-bottom: none

.transport-list__header
  border-bottom: 1px solid #e8e8e8

  .transport-list__row
    padding: 10px 20px

  .transport-list__col
    font-weight: 500

.transport-list__body
  .transport-list__col
    font-size: 13px
    font-weight: 500

.transport-list__row
  display: grid
  grid-template-columns: 320px repeat(4, 1fr)
  gap: 15px
  align-items: center
  padding: 5px 20px

.transport-list__error
  padding: 5px 20px
  color: #e85c20
  font-size: 12px

.transport-list__col--lh0
  line-height: 0

.transport-list__col--center
  text-align: center

.transport-list__name
  padding: 10px 20px 0 20px
  color: #5e5e5e
  font-size: 13px

.transport-list__socials
  display: inline-flex
  gap: 10px
  line-height: 0

  a, button
    display: inline-block
    line-height: 0
    transition: transform 0.3s

    &.grayscale
      pointer-events: none

    &:hover
      transform: translateY(-2px)

.transport-list__status
  display: inline-flex
  gap: 10px
  line-height: 0

// ------------------------ slide-block
.slide-block-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-block-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-block-enter-to, .slide-block-leave
  max-height: 500px
  overflow: hidden

.slide-block-enter, .slide-block-leave-to
  overflow: hidden
  max-height: 0
</style>
